import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useApi, useSeo } from "../lib/settings";
import { crumbs } from "../lib/seo";
import { GameGrid } from "../components/GameGrid";
import { AdSlot } from "../components/AdSlot";

const LISTS = {
  new: { title: "Nuovi Giochi", seo: "Nuovi Giochi Online Gratis", path: "/nuovi-giochi", desc: "Scopri gli ultimi giochi online gratis aggiunti!", params: { sort: "new" } },
  best: { title: "Giochi Migliori", seo: "I Migliori Giochi Online Gratis", path: "/migliori-giochi", desc: "I giochi più popolari del momento, tutti gratis.", params: { sort: "popular" } },
  featured: { title: "Giochi in Evidenza", seo: "Giochi in Evidenza da Giocare Gratis", path: "/giochi-in-evidenza", desc: "La nostra selezione di giochi scelti per te.", params: { featured: true, sort: "new" } },
};

export function ListPage({ kind }) {
  const l = LISTS[kind];
  useSeo({ title: l.seo, description: `${l.desc} Gioca subito dal browser, senza download e senza registrazione.`, path: l.path });
  return (
    <div className="content" data-testid={`list-page-${kind}`}>
      <div className="page-title">
        <h1>{l.title}</h1>
        <p>{l.desc}</p>
      </div>
      <AdSlot name="header_728" className="ad728list" />
      <GameGrid params={l.params} />
    </div>
  );
}

function Crumb({ label, name, to, intro }) {
  return (
    <div className="category-section-top" style={{ textAlign: "center", fontSize: 20, marginBottom: 10, marginTop: 20, color: "white", padding: "0 12px" }}>
      <Link to={to}><i className="fa fa-tags" /> {label}: <i className="fa fa-chevron-right" /></Link>{" "}
      <h1 className="gg-h1" data-testid="listing-title">{name}</h1>
      {intro && <p className="gg-intro" data-testid="listing-intro">{intro}</p>}
    </div>
  );
}

function SeoText({ title, text }) {
  if (!text) return null;
  return (
    <div className="bgs bottomtext fn-clear gg-seo-text" data-testid="category-seo-text">
      <h2>{title}</h2>
      {text.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}
    </div>
  );
}

export function CategoryPage() {
  const { slug } = useParams();
  const cats = useApi("/categories", []);
  const cat = (Array.isArray(cats.data) ? cats.data : []).find((c) => c.slug === slug);
  const heading = cat?.heading || (cat ? `Giochi ${cat.name}` : "");
  const path = `/categoria/${slug}`;
  useSeo({
    title: heading ? `${heading} Gratis Online` : null,
    description: cat?.intro ? `${cat.intro} Gioca gratis senza download.` : undefined,
    path,
    jsonld: heading ? [
      crumbs([["Home", "/"], ["Categorie", "/categorie"], [heading, path]]),
      { "@context": "https://schema.org", "@type": "CollectionPage", name: heading, description: cat?.intro || undefined, inLanguage: "it-IT" },
    ] : null,
  });
  return (
    <div data-testid="category-page">
      <AdSlot name="header_728" className="ad728list" />
      <Crumb label="Categorie" name={heading || slug} to="/categorie" intro={cat?.intro} />
      <div className="content">
        <GameGrid params={{ category: slug, sort: "popular" }} auto={false} />
      </div>
      <SeoText title={`Informazioni sui ${heading}`} text={cat?.description} />
    </div>
  );
}

export function TagPage() {
  const { slug } = useParams();
  const tags = useApi("/tags?limit=600", []);
  const tag = (Array.isArray(tags.data) ? tags.data : []).find((t) => t.slug === slug);
  const name = tag?.name || slug;
  useSeo({
    title: `Giochi ${name} Gratis Online`,
    description: `Gioca gratis ai giochi ${name} online su Giochi.Gratis: direttamente dal browser, senza download e senza registrazione.`,
    path: `/tag/${slug}`,
    jsonld: crumbs([["Home", "/"], ["Tag", "/tags"], [`Giochi ${name}`, `/tag/${slug}`]]),
  });
  return (
    <div data-testid="tag-page">
      <AdSlot name="header_728" className="ad728list" />
      <Crumb label="Tag" name={`Giochi ${name}`} to="/tags" />
      <div className="content">
        <GameGrid params={{ tag: slug, sort: "popular" }} />
      </div>
    </div>
  );
}

export function SearchPage() {
  const [sp] = useSearchParams();
  const q = sp.get("q") || "";
  const [total, setTotal] = useState(null);
  useSeo({ title: `Cerca: ${q}`, description: `Risultati della ricerca per ${q} su Giochi.Gratis.`, noindex: true });
  return (
    <div data-testid="search-page">
      <AdSlot name="header_728" className="ad728list" />
      <h1 style={{ textAlign: "center", fontSize: 20, marginBottom: 10, marginTop: 20, color: "white", fontWeight: 400 }}>
        Risultati per <strong style={{ color: "#fc0" }}>{q}</strong>
        {total != null && <span data-testid="search-total"> ({total})</span>}
      </h1>
      <div className="content" style={{ textAlign: "center" }}>
        {q ? <GameGrid params={{ q, sort: "popular" }} onTotal={setTotal} empty={`Nessun gioco trovato per "${q}".`} /> : <div className="gg-empty">Scrivi qualcosa da cercare.</div>}
      </div>
    </div>
  );
}

export function CategoriesPage({ tags = false }) {
  const { data } = useApi(tags ? "/tags?limit=600" : "/categories", [tags]);
  const list = Array.isArray(data) ? data : [];
  useSeo({
    title: tags ? "Tutti i Tag dei Giochi Gratis" : "Tutte le Categorie di Giochi Gratis",
    description: tags ? "Esplora i giochi gratis per tag: trova subito il tipo di gioco che preferisci." : "Esplora tutte le categorie di giochi gratis: azione, corse, puzzle, sport, giochi per ragazze e molto altro.",
    path: tags ? "/tags" : "/categorie",
  });
  return (
    <div id="Gameinner" className="bgs gg-list" data-testid={tags ? "tags-page" : "categories-page"}>
      <div className="page-title" style={{ marginTop: 0, marginBottom: 20 }}>
        <h1>{tags ? "Tag" : "Categorie"}</h1>
      </div>
      <div className="partners fn-clear">
        {list.map((c) => (
          <Link key={c.slug} to={`/${tags ? "tag" : "categoria"}/${c.slug}`} className="taglink" data-testid={`cat-link-${c.slug}`}>
            <div className="tag">
              {!tags && <div className="tagpic"><img src={c.image} alt={c.name} loading="lazy" /></div>}
              <div className="taginfo">
                <p className="pl">{c.name}</p>
                <p className="num">{(c.total || 0).toLocaleString("it-IT")} Giochi</p>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
